import _ from 'lodash'
import WebsocketHeartbeatJs from 'websocket-heartbeat-js'
import { getToken } from '@pt/utils/auth'
import { language } from '@/common/storage'
import store from '@pt/store'
import { Message } from 'element-ui'
import i18n from '@/lang'
import isMaterialWarehouse from '@/common/is-warehouse'
import { wsHost } from '@/config/host.js'
import { logout } from '@pt/utils/auth'
import router from '../../router'
import websocketConfig from './config'
import { LogoutType } from '@pt/enums/logs'
import { showNetworkExceptionMessage } from '@pt/utils/message'

const { command, code } = websocketConfig

function backToListPage({ msg }) {
  const templateId = store.state.creative.template.templateId
  const pageUtils = {
    'common-template.custom': {
      path: '/my-project/playable/normal',
    },

    'template.custom': {
      path: `/creative-service/playable/${templateId}/version`,
    },
  }

  const page = pageUtils[router.currentRoute.name]

  Message.info({
    message: msg || i18n.t('playTurbo.custom.editing'),
    duration: 2000,
  })
  setTimeout(() => {
    router.replace({
      path: page.path,
      query: page.query || {},
    })
  }, 2000)
}

function cookWebsocketMessage({ router }) {
  if (!router || !router.name) return null

  const dmakerPages = [
    'template.custom',
    'template.dco',
    'template.multi-custom',
  ]
  if (dmakerPages.some((page) => page === router.name)) {
    const customId = Number(_.get(router, 'params.customId'))
    return {
      cmd: command.lockDmaker,
      data: { id: customId },
    }
  }

  const gmakerPages = [
    'common-template.custom',
    'common-template.dco',
    'common-template.multi-custom',
  ]
  if (gmakerPages.some((page) => page === router.name)) {
    const customId = Number(_.get(router, 'params.customId'))
    return {
      cmd: command.lockGmaker,
      data: { id: customId },
    }
  }

  return null
}

const stringifyMessage = (message, router) => {
  const defaultMessage = cookWebsocketMessage({ router })

  if (!_.isEmpty(message)) {
    return JSON.stringify({
      ..._.pick(message, ['id', 'cmd']),
      data: JSON.stringify(message.data),
    })
  }

  if (!_.isEmpty(defaultMessage)) {
    return JSON.stringify({
      ..._.pick(defaultMessage, ['id', 'cmd']),
      data: JSON.stringify(defaultMessage.data),
    })
  }

  return JSON.stringify({ cmd: command.ping })
}

const cmdManager = {
  // 连接
  connect: (data = {}) => {
    // 暂不需要处理 code 不等于 1 的场景
    // {"cmd":"connect", "code":1, "error": "在线人数超出限制"}
    if (data.code === 1) {
      logout({ type: LogoutType.WSError, msg: data.error })
    }
  },

  // 锁定定制模板
  [command.lockDmaker]: (data = {}) => {
    // 锁定成功
    if (data.code === 0) {
      // 锁定模板成功后，unlock 页面
      store.state.creative.template.isLock &&
        store.commit('creative/template/unLock')
      return
    }

    // 页面被编辑中
    if (data.code === code.lockError) {
      store.commit('creative/template/lock')
      backToListPage()
    }

    // 项目不存在于该账号中
    if (data.code === code.projectNotExist) {
      store.commit('creative/template/lock')
      backToListPage({
        msg: '账号下无该项目',
      })
    }
  },

  // 锁定通用模板
  [command.lockGmaker]: (data = {}) => {
    if (data.code === 0) {
      store.state.creative.template.isLock &&
        store.commit('creative/template/unLock')
      return
    }

    // 页面被编辑中
    if (data.code === code.lockError) {
      store.commit('creative/template/lock')
      backToListPage()
    }

    // 项目不存在于该账号中
    if (data.code === code.projectNotExist) {
      store.commit('creative/template/lock')
      backToListPage({
        msg: '账号下无该项目',
      })
    }
  },

  [command.onlineCheck]: (data = {}) => {
    if (data.code === 1) {
      logout({ type: LogoutType.OverLimit, msg: data.error })
    }
  },

  [command.jobUpdate]: (data = {}) => {
    if (data.code === 0) {
      store.commit('pt/websocket/updateJobUpdate', true)
    }
  },

  default: (data = {}) => {
    if (data.code === 1) {
      Message({
        message: data.error || i18n.t('common.request.networkError'),
        type: 'error',
        duration: 5 * 1000,
      })
    }
  },
}

let heartBeatSocket = null
export default {
  namespaced: true,

  state: {
    isConnected: false,
    /**
     * @types {cmd: string, data: {}, id: string}
     */
    message: {},
    // 默认消息，当 message 为空或空对象时，才使用该消息
    defaultMessage: {},
    connectError: false,
    timeId: 0,
    router: null,
    isHeartBeatActive: false,
    // 任务中心状态变更
    jobUpdate: false,
  },

  mutations: {
    updateMessage(state, value) {
      state.message = value
    },

    // 更新路由，每次进入一个新的页面都需要更新，保证 sendMessage 拿到的是正确的路由信息
    updateRouter(state, router) {
      state.router = router
    },

    updateJobUpdate(state, value) {
      state.jobUpdate = value
    },
  },

  actions: {
    activeHeartBeat({ state, commit, dispatch }, { router }) {
      commit('updateRouter', router)
      if (isMaterialWarehouse()) return
      if (!getToken()) return
      if (state.isHeartBeatActive) return

      return dispatch('sendMessage', {
        heartBeat: true,
        useDefaultHandler: true,
      })
    },

    /**
     * @param message any
     * @param heartBeat boolean
     * @param useDefaultHandler boolean 是否将 message resolve 出去
     * @returns {Promise<unknown>}
     */
    sendMessage(
      { state, commit },
      { message, heartBeat = false, useDefaultHandler = false },
    ) {
      if (!getToken()) return

      // eslint-disable-next-line consistent-return
      return new Promise((resolve, reject) => {
        commit('updateMessage', message)

        const maxRetry = 3
        let retryCount = 0

        const onClose = (event) => {
          console.log('ws close...', event)
        }

        const onReconnect = () => {
          console.log('ws reconnecting...')
        }

        const onError = (err) => {
          commit('updateMessage', {})
          console.log('websocket onerror', err)
          retryCount += 1
          if (retryCount >= maxRetry) {
            // logout({ type: LogoutType.WSError, msg: 'Network Error' })
            showNetworkExceptionMessage()
          }
          state.connetError = true
        }

        const onOpen = () => {
          console.log('connect success')
          // 重联成功后重置重连次数
          retryCount = 0
          heartBeatSocket.send(stringifyMessage(state.message, state.router))

          // 开启心跳包
          if (heartBeat && !state.isHeartBeatActive) {
            console.log('激活心跳包')
            state.isHeartBeatActive = true
            clearInterval(state.timeId)
            setInterval(() => {
              if (heartBeatSocket.ws.readyState === WebSocket.OPEN) {
                heartBeatSocket.send(
                  stringifyMessage(state.message, state.router),
                )
              }
            }, 3000)
          }
        }

        const onMessage = (e) => {
          // 接收到消息时，重置 message，保证不会发送到上次已发送的消息
          state.message = {}
          let data = null
          try {
            data = JSON.parse(e.data)
          } catch (err) {
            console.error(err)
            data = null
          }

          if (!data) return

          if (!useDefaultHandler) {
            resolve(data)
          } else {
            if (_.isFunction(cmdManager[data.cmd])) {
              cmdManager[data.cmd](data)
            } else {
              cmdManager.default(data)
            }
            resolve(data)
          }
        }

        if (heartBeatSocket) {
          if (heartBeatSocket.ws.readyState === WebSocket.OPEN) {
            console.log('send:', state.message)
            heartBeatSocket.send(stringifyMessage(state.message, state.router))
          }
        } else {
          const params = {
            url: `${wsHost}/ws?_token=${getToken()}&lang=${language.get()}`,
            pingTimeout: 15000, // pingTimeout 未收到消息多少秒之后发送ping请求，默认15000毫秒
            pongTimeout: 10000, // pongTimeout  发送ping之后，未收到消息超时时间，默认10000毫秒
            reconnectTimeout: 3000,
            pingMsg: JSON.stringify({ cmd: command.ping }),
            repeatLimit: maxRetry,
          }
          heartBeatSocket = new WebsocketHeartbeatJs(params)
        }

        heartBeatSocket.onopen = onOpen
        heartBeatSocket.onreconnect = onReconnect
        heartBeatSocket.onerror = onError
        heartBeatSocket.onmessage = onMessage
        heartBeatSocket.onclose = onClose
      })
    },

    // 在线人数检测
    async onlineCheck({ dispatch }, { module } = {}) {
      if (!module) {
        throw new Error('module is required')
      }

      const message = {
        cmd: command.onlineCheck,
        data: {
          module,
        },
      }

      return await store.dispatch('pt/websocket/sendMessage', {
        message,
      })
    },
  },
}
